import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

// P1-C — "Gelişim Grafiğim": çalışanın kapalı dönemlerdeki skor trendi.
// Veri BE'nin KVKK-korumalı history ucundan gelir (FinalLevel ASLA yok).
// Grafik/tema deseni DistributionChartCard ile birebir: renkler yalnız theme
// token'larından (hardcode YASAK), eksen/grid renkleri theme.colors.surface/
// text token'ları. İki seri farklı ölçek taşır (Sistem A 0-100, 360° 1-5) —
// ayrı yAxisId ile normalize edilmeden çizilir.
const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
`;

const CardMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 12px;
`;

export default function TrendCard({ history }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const chartData = useMemo(() => (history || []).map((item) => ({
    name: item.name,
    systemAScore: item.systemAScore != null ? Number(item.systemAScore) : null,
    weightedOverall: item.weightedOverall != null ? Number(item.weightedOverall) : null,
  })), [history]);

  if (chartData.length === 0) return null;

  const hasA = chartData.some((d) => d.systemAScore != null);
  const hasB = chartData.some((d) => d.weightedOverall != null);

  return (
    <Card>
      <CardTitle>{t('performance.results.trendTitle')}</CardTitle>
      <CardMeta>{t('performance.results.trendMeta')}</CardMeta>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.surface.border} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: theme.colors.text.muted }}
            axisLine={{ stroke: theme.colors.surface.border }}
            tickLine={false}
          />
          <YAxis yAxisId="a" hide domain={[0, 100]} />
          <YAxis yAxisId="b" hide domain={[0, 5]} orientation="right" />
          <Tooltip cursor={{ stroke: theme.colors.surface.border }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {hasA && (
            <Line
              yAxisId="a"
              type="monotone"
              dataKey="systemAScore"
              name={t('performance.results.trendSystemA')}
              stroke={theme.colors.primary.main}
              strokeWidth={2}
              dot={{ r: 3, fill: theme.colors.primary.main }}
              connectNulls
            />
          )}
          {hasB && (
            <Line
              yAxisId="b"
              type="monotone"
              dataKey="weightedOverall"
              name={t('performance.results.trendSystemB')}
              stroke={theme.colors.auxiliar.purple}
              strokeWidth={2}
              dot={{ r: 3, fill: theme.colors.auxiliar.purple }}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
